import { invoicePaymentMethodLabels } from '@/constants'
import { Invoice, InvoicePaymentMethod, SharedProps } from '@/types'
import { formatCurrency } from '@/utils/format'
import { router, usePage } from '@inertiajs/react'
import { Button, Group, Modal, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import dayjs from 'dayjs'

const PAYMENT_METHOD_OPTIONS = Object.entries(invoicePaymentMethodLabels).map(([value, label]) => ({ value, label }))

function PaymentForm({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const { current_organization } = usePage<SharedProps>().props
  const amountDue = Number(invoice.amount_due)
  const form = useForm({
    initialValues: {
      amount: amountDue,
      paid_on: dayjs().format('YYYY-MM-DD'),
      payment_method: 'transfer' as InvoicePaymentMethod,
    },
    validate: {
      amount: (value) => (value > 0 && value <= amountDue ? null : `Entre 0 et ${formatCurrency(amountDue)}`),
      paid_on: (value) => (value ? null : 'Date requise'),
    },
  })

  const handlePaymentMethodChange = (value: string | null) =>
    form.setFieldValue('payment_method', (value ?? 'transfer') as InvoicePaymentMethod)

  const handleSubmit = (values: typeof form.values) =>
    router.post(
      `/organizations/${current_organization?.slug}/invoices/${invoice.id}/payments`,
      { payment: values },
      { onSuccess: onClose },
    )

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Reste à encaisser : {formatCurrency(amountDue)}. Quand la TVA est due à l'encaissement, le paiement est
          aussi déclaré à la plateforme.
        </Text>
        <NumberInput
          label="Montant reçu"
          suffix=" €"
          decimalScale={2}
          decimalSeparator=","
          min={0}
          {...form.getInputProps('amount')}
        />
        <TextInput type="date" label="Date de réception" {...form.getInputProps('paid_on')} />
        <Select
          label="Moyen de paiement"
          data={PAYMENT_METHOD_OPTIONS}
          allowDeselect={false}
          value={form.values.payment_method}
          onChange={handlePaymentMethodChange}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit">Enregistrer le paiement</Button>
        </Group>
      </Stack>
    </form>
  )
}

export default function PaymentModal({ invoice, onClose }: { invoice: Invoice | null; onClose: () => void }) {
  return (
    <Modal opened={!!invoice} onClose={onClose} title={`Encaissement — ${invoice?.number ?? ''}`} centered>
      {invoice && <PaymentForm key={invoice.id} invoice={invoice} onClose={onClose} />}
    </Modal>
  )
}
